/**
 * 表单提交 Hook
 *
 * 职责：
 * 1. 校验表单
 * 2. 组装提交数据
 * 3. 调用保存回调
 */
import { useState, useCallback } from 'react';
import { FormInstance } from 'antd/es/form';
import {
  AnchorLayerType,
  EffectDateType,
  EffectTimeType,
  EffectVehicleType,
  AttributeTypeText,
} from '@/utils/constant';
import { normalizeVehicleLimit } from '../utils/vehicleLimit';

interface UseFormSubmitOptions {
  form: FormInstance;
  saveFormInfo: (data: any) => Promise<boolean> | boolean | void;
  roadTestItems?: Array<{ itemNo: string; itemName: string }>;
}

export const useFormSubmit = ({ form, saveFormInfo, roadTestItems = [] }: UseFormSubmitOptions) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      // 校验分时段有效性
      const timeLimit = values.timeLimit || [];
      const hasInvalidTime =
        timeLimit.filter((v: any) => !v.value || v.errorMes).length > 0;

      if (values.effectTimeType === EffectTimeType.PERIOD_TIME && hasInvalidTime) {
        return;
      }

      // 组装提交数据
      const submitValue: any = { ...values };

      // 时间限制造空
      submitValue.timeLimit =
        hasInvalidTime || submitValue.effectDateType === EffectDateType.PERMANENT
          ? null
          : submitValue.timeLimit?.map((t: any) => t.value.join('-'));

      // 长期生效删除 weekLimit
      if (submitValue.effectDateType === EffectDateType.PERMANENT) {
        delete submitValue.weekLimit;
      }

      // 非限速类型删除车道相关字段
      if (submitValue.type !== AnchorLayerType.SPEED_LIMIT) {
        delete submitValue.laneIdList;
        delete submitValue.speedLimit;
      }

      // 全部生效不提交车号；部分生效过滤接口/历史数据里的空字符串
      submitValue.vehicleLimit = submitValue.effectVehicleType === EffectVehicleType.ALL_EFFECT
        ? undefined
        : normalizeVehicleLimit(submitValue.vehicleLimit);

      // 非封路障碍物类型删除生效车范围
      if (submitValue.type !== AnchorLayerType.ROAD_CLOSED) {
        delete submitValue.effectiveMapList;
      }

      // 添加 subTypeName 字段
      if (submitValue.type === AnchorLayerType.ATTRIBUTE_ANNOTATION && submitValue.subType) {
        // 属性标注区：从枚举中获取名称
        submitValue.subTypeName = AttributeTypeText.get(submitValue.subType) || '';
      } else if (submitValue.type === AnchorLayerType.ROAD_TEST_FOCUS_AREA && submitValue.subType) {
        // 路测关注区：从测试项列表中获取名称
        const item = roadTestItems.find((i) => i.itemNo === submitValue.subType);
        submitValue.subTypeName = item?.itemName || '';
      }

      setIsSubmitting(true);
      try {
        const result = saveFormInfo(submitValue);
        const success = await Promise.resolve(result);
        if (success === false) {
          setIsSubmitting(false);
        }
      } catch (err) {
        console.error('表单提交失败:', err);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('表单校验失败:', err);
    }
  }, [form, saveFormInfo, roadTestItems]);

  return { isSubmitting, handleSubmit };
};
